import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataList } from "@/components/common/DataList";
import { PageHeader, Pagination, YesNoBadge } from "@/components/common/Common";
import { useAuth } from "@/context/AuthContext";
import { useList } from "@/hooks/useList";
import { CompanyFormDialog } from "./CompanyFormDialog";

export default function RecruitmentCompanies() {
  const { canWrite } = useAuth();
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState("true");
  const filters = { is_recruitment_company: true, ...(active !== "all" ? { active } : {}) };
  const list = useList("/companies", { filters });
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    const id = params.get("edit");
    if (id) setEditing({ id });
  }, [params]);

  const close = () => { setEditing(null); if (params.get("edit")) setParams({}); };
  const columns = [
    { key: "name", label: "Name", render: (r) => <span className="font-medium text-slate-900">{r.name}</span> },
    { key: "abn", label: "ABN", render: (r) => <span className="font-mono text-xs">{r.abn || "—"}</span> },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "state", label: "State", render: (r) => r.address?.state || "—" },
    { key: "is_approved", label: "Approved", render: (r) => <YesNoBadge value={r.is_approved} /> },
    { key: "active", label: "Active", render: (r) => <YesNoBadge value={r.active} /> },
  ];

  return (
    <div>
      <PageHeader title="Recruitment Companies" subtitle="Companies that place contractors with PayMe." testId="recruitment-companies-header"
        actions={canWrite && <Button onClick={() => setEditing({})} data-testid="create-company-button"><Plus className="h-4 w-4 mr-1" /> New company</Button>} />
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input placeholder="Search name, ABN or email…" value={list.q} onChange={(e) => list.setQ(e.target.value)} className="sm:max-w-sm bg-white" data-testid="company-search-input" />
        <Select value={active} onValueChange={setActive}>
          <SelectTrigger className="sm:w-44 bg-white" data-testid="company-active-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Active only</SelectItem>
            <SelectItem value="false">Inactive only</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <DataList columns={columns} rows={list.items} loading={list.loading} onRowClick={(r) => setEditing(r)} rowTestId="company-row" />
      <Pagination {...list} testId="company-pagination" />
      {editing && <CompanyFormDialog initial={editing} onClose={close} onSaved={() => { close(); list.reload(); }} />}
    </div>
  );
}
